import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, Clock, MessageSquare, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "General Plumbing",
  "Hot Water Service",
  "Gas",
  "CCTV Drain Camera",
  "High-Pressure Water Jetter",
  "Drainage",
  "Water Filtration",
  "Renovations",
  "Other / Not Sure",
];

const EMPTY = { name: "", phone: "", service: SERVICE_OPTIONS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number so we can call you back.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/quotes`, form);
      toast.success(res.data.message || "Quote request sent. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#111111]/20 px-5 py-4 text-sm font-medium placeholder:text-[#111111]/35 focus:outline-none focus:ring-2 focus:ring-[#003BFF] focus:border-[#003BFF] transition-[box-shadow,border-color] duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#111111] text-[#F2F2F0]">
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="px-6 md:px-12 py-20 md:py-28 lg:border-r border-white/15">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-xs font-bold uppercase tracking-[0.3em] text-[#6C8CFF] mb-6"
          >
            Get a Quote
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display uppercase tracking-wide leading-[1.02] text-4xl md:text-6xl mb-8"
          >
            Tell Us The Job.<br />
            <span className="text-[#6C8CFF]">We'll Call You Back.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-white/60 font-medium leading-relaxed max-w-md mb-14"
          >
            Send through the details and we'll get back to you with an honest,
            upfront quote — no obligation.
          </motion.p>

          <div className="space-y-8">
            {[
              { icon: MessageSquare, title: "Fast Call Back", desc: "We respond to every quote request promptly." },
              { icon: MapPin, title: "Local Service Area", desc: "Proudly servicing the local community and surrounds." },
              { icon: Clock, title: "Flexible Booking", desc: "We work around your schedule, including urgent jobs." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                data-testid={`contact-info-${i}`}
                className="flex items-start gap-5"
              >
                <span className="w-11 h-11 shrink-0 border border-white/20 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#6C8CFF]" />
                </span>
                <div>
                  <h3 className="font-bold uppercase tracking-[0.12em] text-sm mb-1">{item.title}</h3>
                  <p className="text-sm text-white/50 font-medium">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <a
            href="tel:+61452028256"
            data-testid="contact-phone-link"
            className="mt-12 inline-flex items-center gap-5 group w-fit"
          >
            <span className="w-14 h-14 shrink-0 bg-[#003BFF] rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <Phone className="w-6 h-6 text-white" />
            </span>
            <span>
              <span className="block text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-1">
                Call Us Direct
              </span>
              <span className="font-display text-3xl tracking-wide group-hover:text-[#6C8CFF] transition-colors duration-300">
                0452 028 256
              </span>
            </span>
          </a>
          <a
            href="mailto:onepiecemaintenance@gmail.com"
            data-testid="contact-email-link"
            className="mt-6 inline-flex items-center gap-5 group w-fit"
          >
            <span className="w-14 h-14 shrink-0 border border-white/20 rounded-full flex items-center justify-center transition-colors duration-300 group-hover:border-[#6C8CFF]">
              <Mail className="w-6 h-6 text-[#6C8CFF]" />
            </span>
            <span>
              <span className="block text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-1">
                Email Us
              </span>
              <span className="font-bold text-lg tracking-wide group-hover:text-[#6C8CFF] transition-colors duration-300 break-all">
                onepiecemaintenance@gmail.com
              </span>
            </span>
          </a>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={handleSubmit}
          data-testid="quote-form"
          className="px-6 md:px-12 py-20 md:py-28 flex flex-col justify-center gap-6"
        >
          <div>
            <label htmlFor="quote-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
              Your Name *
            </label>
            <input
              id="quote-name"
              data-testid="quote-name-input"
              value={form.name}
              onChange={set("name")}
              placeholder="Jane Citizen"
              className={inputCls + " text-[#111111]"}
            />
          </div>
          <div>
            <label htmlFor="quote-phone" className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
              Phone Number *
            </label>
            <input
              id="quote-phone"
              data-testid="quote-phone-input"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="0400 000 000"
              className={inputCls + " text-[#111111]"}
            />
          </div>
          <div>
            <label htmlFor="quote-service" className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
              Service Needed
            </label>
            <select
              id="quote-service"
              data-testid="quote-service-select"
              value={form.service}
              onChange={set("service")}
              className={inputCls + " text-[#111111] appearance-none cursor-pointer"}
            >
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="quote-message" className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
              Tell Us More
            </label>
            <textarea
              id="quote-message"
              data-testid="quote-message-input"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="e.g. Kitchen sink is blocked and draining slowly..."
              className={inputCls + " text-[#111111] resize-none"}
            />
          </div>
          <button
            type="submit"
            data-testid="quote-submit-button"
            disabled={sending}
            className="group mt-2 bg-[#003BFF] text-white rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-2 hover:-translate-y-1 transition-transform duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                Request My Free Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
